/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, AdminRole } from './contexts/AuthContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DriverManagement from './pages/DriverManagement';
import DriverApproval from './pages/DriverApproval';
import TripManagement from './pages/TripManagement';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import SystemStatus from './pages/SystemStatus';
import DriverMap from './pages/DriverMap';
import ErrorLogs from './pages/ErrorLogs';
import LoadingScreen from './components/LoadingScreen';
import Unauthorized from './pages/Unauthorized';
import CashoutManagement from './pages/CashoutManagement';
import VehicleApproval from './pages/VehicleApproval';
import SOSAlerts from './pages/SOSAlerts';
import UserManagement from './pages/UserManagement';
import AccountDeletionRequests from './pages/AccountDeletionRequests';
import AdminPortalUsers from './pages/AdminPortalUsers';
import Documentation from './pages/Documentation';

// Wraps a route element — renders Unauthorized if the user lacks the required roles
function RoleGuard({ roles, children }: { roles: AdminRole[]; children: JSX.Element }) {
  const { hasRole } = useAuth();
  return hasRole(...roles) ? children : <Unauthorized />;
}

function App() {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (!isAdmin) {
    return (
      <Routes>
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<Navigate to="/unauthorized" replace />} />
      </Routes>
    );
  }

  return (
    <ErrorBoundary>
      <Layout>
        <Routes>
          {/* All roles */}
          <Route path="/" element={<Dashboard />} />

          {/* Operations + SuperAdmin */}
          <Route path="/drivers" element={
            <RoleGuard roles={['SuperAdmin', 'Operations', 'CustomerService', 'TechSupport']}>
              <DriverManagement />
            </RoleGuard>
          } />
          <Route path="/driver-approval" element={
            <RoleGuard roles={['SuperAdmin', 'Operations']}>
              <DriverApproval />
            </RoleGuard>
          } />
          <Route path="/vehicle-approval" element={
            <RoleGuard roles={['SuperAdmin', 'Operations']}>
              <VehicleApproval />
            </RoleGuard>
          } />
          <Route path="/driver-map" element={
            <RoleGuard roles={['SuperAdmin', 'Operations']}>
              <DriverMap />
            </RoleGuard>
          } />

          {/* CustomerService + SuperAdmin */}
          <Route path="/users" element={
            <RoleGuard roles={['SuperAdmin', 'CustomerService']}>
              <UserManagement />
            </RoleGuard>
          } />
          <Route path="/account-deletions" element={
            <RoleGuard roles={['SuperAdmin', 'CustomerService']}>
              <AccountDeletionRequests />
            </RoleGuard>
          } />
          <Route path="/sos-alerts" element={
            <RoleGuard roles={['SuperAdmin', 'Operations', 'CustomerService']}>
              <SOSAlerts />
            </RoleGuard>
          } />

          {/* Trips — most roles */}
          <Route path="/trips" element={
            <RoleGuard roles={['SuperAdmin', 'Operations', 'CustomerService', 'Finance']}>
              <TripManagement />
            </RoleGuard>
          } />

          {/* Finance + SuperAdmin */}
          <Route path="/cashout" element={
            <RoleGuard roles={['SuperAdmin', 'Finance']}>
              <CashoutManagement />
            </RoleGuard>
          } />
          <Route path="/analytics" element={
            <RoleGuard roles={['SuperAdmin', 'Operations', 'Finance']}>
              <Analytics />
            </RoleGuard>
          } />

          {/* SuperAdmin + Operations (pricing & service areas inside Settings) */}
          <Route path="/settings" element={
            <RoleGuard roles={['SuperAdmin', 'Operations', 'Finance']}>
              <Settings />
            </RoleGuard>
          } />

          {/* TechSupport + SuperAdmin */}
          <Route path="/status" element={
            <RoleGuard roles={['SuperAdmin', 'TechSupport']}>
              <SystemStatus />
            </RoleGuard>
          } />
          <Route path="/error-logs" element={
            <RoleGuard roles={['SuperAdmin', 'TechSupport']}>
              <ErrorLogs />
            </RoleGuard>
          } />

          {/* SuperAdmin only */}
          <Route path="/portal-users" element={
            <RoleGuard roles={['SuperAdmin']}>
              <AdminPortalUsers />
            </RoleGuard>
          } />

          {/* SuperAdmin + TechSupport */}
          <Route path="/docs" element={
            <RoleGuard roles={['SuperAdmin', 'TechSupport']}>
              <Documentation />
            </RoleGuard>
          } />

          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </ErrorBoundary>
  );
}

export default App;
